import { useNavigate } from 'react-router-dom';
import { openTarpNavState, tarpEntry } from '@/domain';
import {
  agruparPorEstagio, ESTAGIO_LABEL, STATUS_LABEL,
  type EstagioTarp, type TarpRecordStatus,
} from '@/domain/tarp/status';
import { LEVEL_UI } from '@/ui';
import { formatarNumeroTarp } from '@/domain/tarp/numero';
import type { TarpsResumo } from './useM1Data';

/** Cor por status na lista de ativas (alinhado ao "Resumo das TARPs" do V8). */
const STATUS_COLOR: Record<TarpRecordStatus, string> = {
  EM_ABERTURA: '#94a3b8',
  ABERTA: '#f97316',
  EM_ANALISE_RT: '#78B4CF',
  CONCLUIDA_AGUARDANDO: '#eab308',
  ENCERRADA: '#22c55e',
};

/**
 * Cinza `jf-muted` — a cor de "não há o que sinalizar aqui". É o que a pill usa
 * quando a contagem é zero, e o que "Baixadas" usa sempre.
 */
const NEUTRO = '#94a3b8';

/**
 * As 3 pills do resumo — GUPM-325.
 *
 * Eram 4, uma por ESTADO do enum, e o resultado em produção (31/08/2026) era:
 * `Abertas 0`, `Em análise RT 0`, `Aguard. baixa 2`, `Encerradas 4` — duas colunas
 * estruturalmente zero (`EM_ANALISE_RT` é declarado legado no próprio domínio),
 * enquanto **4 TARPs em `EM_ABERTURA`** existiam no banco e não apareciam em
 * contador nenhum.
 *
 * Passam a contar por ESTÁGIO (`ESTAGIO_TARP`), o que cobre os cinco valores do
 * enum: nenhuma TARP fica sem contador. O estado individual exato continua legível
 * logo abaixo, na lista de ativas, que já traz a tarja por status.
 *
 * `cor` só é usada quando a contagem é > 0 (`Baixadas` nunca acende: verde
 * chamativo para o que já foi resolvido competia com o que ainda pede ação). Nenhuma
 * pill tem fundo tintado — o `#78B4CF` é cor local do resumo, não `LEVEL_UI`.
 */
// A ordem é a da tela e está fixada em `tarp-pill-ordem-aguardando-rt-primeiro`:
// "Aguardando RT" abre a fileira porque é o estágio que a LISTA logo abaixo detalha
// (ela mostra só as que aguardam o RT); "Baixadas" fecha, por não pedir ação.
const ESTAGIOS: { estagio: EstagioTarp; cor: string }[] = [
  { estagio: 'AGUARDANDO_RT', cor: '#78B4CF' },
  { estagio: 'EM_ABERTURA', cor: '#f97316' },
  { estagio: 'ENCERRADA', cor: NEUTRO },
];

/**
 * Resumo das TARPs no dashboard (GUPM-191 D3, fiel a `buildTarpSummary` do V8):
 * pills por estágio (número em cima, rótulo embaixo) + lista das ativas (até 4,
 * com "+N outra(s)") + atalho pro M3. Sem badge "N ativas" — o V8 não tem (o total
 * contava rascunhos EM_ABERTURA sem exibi-los; achado #1 do review). Read-only.
 */
export default function TarpSummaryCard({ resumo }: { resumo: TarpsResumo }) {
  const navigate = useNavigate();
  const restantes = resumo.ativasTotal - resumo.ativas.length;
  const porEstagio = agruparPorEstagio(resumo.porStatus);
  return (
    <div className="rounded-xl border border-jf-border bg-jf-surface p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-white">
          🛡️ Resumo das TARPs
        </h2>
        <button
          type="button"
          onClick={() => navigate('/tarp')}
          className="text-[11px] font-bold text-jf-muted hover:text-white transition-colors"
        >
          Ver módulo TARP →
        </button>
      </div>

      {/* Pills por estágio — número grande em cima, rótulo embaixo. Fundo neutro:
          a cor fica só no número, e só quando há o que contar. */}
      <div className="flex flex-wrap gap-2 mb-3">
        {ESTAGIOS.map(({ estagio, cor }) => {
          const n = porEstagio[estagio];
          return (
            <div
              key={estagio}
              data-testid={`pill-${estagio}`}
              className="flex-1 min-w-[70px] rounded-[10px] border border-jf-border px-1.5 py-2.5 text-center"
            >
              <div
                data-testid={`pill-valor-${estagio}`}
                className="text-xl font-black leading-none"
                style={{ color: n > 0 ? cor : NEUTRO }}
              >
                {n}
              </div>
              <div className="mt-0.5 text-[10px] font-semibold leading-tight text-jf-muted">
                {ESTAGIO_LABEL[estagio]}
              </div>
            </div>
          );
        })}
      </div>

      {resumo.ativas.length === 0 ? (
        <p className="text-center text-xs text-level-normal">✅ Nenhuma TARP ativa no momento.</p>
      ) : (
        <ul className="space-y-1.5">
          {resumo.ativas.map((t) => {
            // Cor do item = nível do TIPO de TARP (V8: LEVEL_UI[TARPS[tarpId].nivel]).
            const nivel = tarpEntry(t.tarpCode)?.nivel ?? 'ATENCAO';
            const ui = LEVEL_UI[nivel];
            // ID corrido TR-000N (o banco só guarda `seq`; fiel ao r.id do V8).
            const trId = formatarNumeroTarp(t.seq);
            return (
              <li key={t.id}>
                <button
                  type="button"
                  data-level={nivel}
                  // GUPM-199: deep-link — abre ESTA TARP já aberta no M3 (canal efêmero
                  // via router state; o M3 acha na lista e reusa o clique da lista).
                  onClick={() => navigate('/tarp', { state: openTarpNavState(t.id) })}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-left transition hover:brightness-110"
                  style={{ background: ui.bg, borderColor: ui.border }}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 font-mono text-xs font-bold" style={{ color: ui.color }}>
                      {trId}
                    </span>
                    <span className="min-w-0 truncate text-xs text-slate-200">
                      {t.tarpCode}
                      {t.instrumentCode && <span className="text-jf-muted"> · {t.instrumentCode}</span>}
                    </span>
                  </span>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{ color: STATUS_COLOR[t.status], background: `${STATUS_COLOR[t.status]}1a` }}
                  >
                    {STATUS_LABEL[t.status]}
                  </span>
                </button>
              </li>
            );
          })}
          {restantes > 0 && (
            <li className="pt-0.5 text-center text-[11px] text-jf-muted">
              +{restantes} outra{restantes > 1 ? 's' : ''} no módulo TARP
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
